/**
 * CetusEd NextGen — sign-in form.
 *
 * Accessible email+password form that calls useLogin, shows a loading state,
 * and on success stores the session (inside signIn) and navigates to the
 * authenticated landing. On failure it shows a GENERIC error and does NOT
 * navigate: the message never reveals which field was wrong, mirroring the
 * server's single generic 401 (no user-enumeration oracle). ⛔4 — the UI only
 * advances on the REAL API result.
 */
import { useState } from 'react'
import type { CSSProperties, FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { AppError } from '@cetused/shared'
import { useLogin } from '../../api/hooks/useAuth'

const ERROR_ID = 'login-error'

function errorMessage(error: unknown): string {
  // Rate limiting is about REQUEST VOLUME, not credential correctness, so
  // naming it is not an enumeration oracle.
  if (error instanceof AppError && error.statusCode === 429) {
    return 'Too many attempts. Please wait a moment and try again.'
  }
  // A 403 here is NOT a credential problem — it means the server could not
  // determine the tenant (the environment/hostname is not set up), so telling
  // the user to "check your email and password" sends them chasing the wrong
  // thing. This reveals nothing about whether an account exists (no enumeration
  // oracle): it fires on ANY credentials before the password is ever checked.
  if (error instanceof AppError && error.statusCode === 403) {
    return 'This environment is not fully set up yet (no matching organization). Contact your administrator.'
  }
  // 401 and everything else: one generic line (never reveal which field).
  return 'Sign in failed. Check your email and password and try again.'
}

export function LoginForm() {
  const navigate = useNavigate()
  const login = useLogin()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const showError = login.isError && !login.isPending

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    login.mutate(
      { email, password },
      { onSuccess: () => navigate('/', { replace: true }) },
    )
  }

  return (
    <main style={styles.wrap}>
      <form
        style={styles.card}
        onSubmit={onSubmit}
        aria-labelledby="login-heading"
        aria-describedby={showError ? ERROR_ID : undefined}
        noValidate
      >
        <h1 id="login-heading" style={styles.heading}>Sign in to CetusEd</h1>

        <label htmlFor="login-email" style={styles.label}>Email</label>
        <input
          id="login-email"
          name="email"
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={styles.input}
          disabled={login.isPending}
        />

        <label htmlFor="login-password" style={styles.label}>Password</label>
        <input
          id="login-password"
          name="password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          style={styles.input}
          disabled={login.isPending}
        />

        {showError ? (
          <p id={ERROR_ID} role="alert" style={styles.error}>
            {errorMessage(login.error)}
          </p>
        ) : null}

        <button type="submit" style={styles.button} disabled={login.isPending}>
          {login.isPending ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </main>
  )
}

const styles: Record<string, CSSProperties> = {
  wrap: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1.5rem',
    background: 'var(--navy, #0f2a4a)',
  },
  card: {
    width: '100%',
    maxWidth: '22rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    padding: '2rem',
    borderRadius: '0.75rem',
    background: '#fff',
    boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
  },
  heading: { margin: '0 0 0.5rem', fontSize: '1.25rem', color: '#0f2a4a' },
  label: { fontSize: '0.85rem', fontWeight: 600, color: '#334155' },
  input: {
    padding: '0.55rem 0.65rem',
    fontSize: '1rem',
    borderRadius: '0.4rem',
    border: '1px solid #cbd5e1',
    marginBottom: '0.35rem',
  },
  error: { margin: '0.25rem 0', color: '#b91c1c', fontSize: '0.9rem' },
  button: {
    marginTop: '0.75rem',
    padding: '0.6rem 0.75rem',
    fontSize: '1rem',
    fontWeight: 600,
    color: '#fff',
    background: 'var(--teal, #0d7d8a)',
    border: 'none',
    borderRadius: '0.45rem',
    cursor: 'pointer',
  },
}
